import { FC, Fragment, useCallback } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDebouncedSave } from 'hooks/useDebounceSave';

import { FieldTable2 } from 'components/_basic/Form/FieldTable2';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { IPeriodsTableRows } from 'components/BusinessToolForm/constants/periods';
import {
  GLOBAL_PERIOD_NAME,
  GLOBAL_PERIOD_TABLE_HEADINGS_NAME,
} from 'components/BusinessToolForm/Sections/Global/form';
import { PERIODS_OPERATION_TYPES, summarizeSubtractPeriodValues } from 'components/BusinessToolForm/utils/helpers';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { ICalendarPlanFormProps } from './types';
import { CALENDAR_PLAN_NAMES } from './utils/form';
import { FullScreenWrapper } from '../../../components/FullScreenWrapper';
import { useUnitChange } from '../../../hooks/useUnitChange';
import { FINANCING_ACTIVITIES_NAMES } from '../../../ProjectedCashFlow/DirectForm/DirectFormComponents/Sections/FinancingActivities/utils/form';
import { financingActivitiesPrefix } from '../../../ProjectedCashFlow/utils/form';
import { calendarPlanPrefix, FIRST_COLUMN_WIDTH } from '../../utils/form';
import { PeriodFormHeader } from '../components/PeriodFormHeader';

export const CalendarPlanForm: FC<ICalendarPlanFormProps> = ({ disabled }) => {
  const { t } = useTranslation();
  const { key } = useUnitChange();

  const { control, setValue } = useFormContext();

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const columnsNames = getPeriodColumnsNames(period);

  const tableName = concatWithDot(calendarPlanPrefix, CALENDAR_PLAN_NAMES.TABLE);
  const totalName = concatWithDot(calendarPlanPrefix, CALENDAR_PLAN_NAMES.TOTAL);

  const { fields } = useFieldArray({ control, name: tableName });
  const calendarPlanTable = useWatch({ name: tableName });

  const variantsName = concatWithDot(calendarPlanPrefix, CALENDAR_PLAN_NAMES.VARIANTS, ':index');

  const issuanceDebtField = concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.PROCEEDS_ISSUANCE_DEBT);
  const issuanceEquityField = concatWithDot(
    financingActivitiesPrefix,
    FINANCING_ACTIVITIES_NAMES.PROCEEDS_ISSUANCE_EQUITY,
  );

  const calendarHandler = useCallback((values: IPeriodsTableRows[]) => {
    const ownFunds = values[0];
    const borrowedFunds = values[1];
    const investmentFunds = values[2];
    const issuanceEquity = summarizeSubtractPeriodValues([ownFunds, investmentFunds], PERIODS_OPERATION_TYPES.ADD);

    setValue(issuanceDebtField, borrowedFunds);
    setValue(issuanceEquityField, issuanceEquity);
  }, []);

  const totalFiledHandler = useCallback((values: IPeriodsTableRows[]) => {
    const totalValue = summarizeSubtractPeriodValues(values, PERIODS_OPERATION_TYPES.ADD);
    setValue(totalName, totalValue);
  }, []);

  useDebouncedSave({
    data: calendarPlanTable,
    saveFunction: calendarHandler,
    debounceTime: 500,
    skipExecutionOnFirstRender: true,
  });
  useDebouncedSave({
    data: calendarPlanTable,
    saveFunction: totalFiledHandler,
    debounceTime: 500,
    skipExecutionOnFirstRender: true,
  });

  return (
    <FormSectionCard collapsible title={t('financialPlan.cashFlowElements.calendarPlan.title')} disabled={disabled}>
      <FullScreenWrapper title={t('financialPlan.cashFlowElements.calendarPlan.title')} disabled={disabled}>
        <Fragment key={key}>
          <PeriodFormHeader
            disabled={disabled}
            periodId="calendarPlanFormHeader.period"
            currencyUnitName={concatWithDot(calendarPlanPrefix, CALENDAR_PLAN_NAMES.CURRENCY)}
          />
          <FormSectionFilledCard>
            <FieldTable2
              showSumRow
              name={tableName}
              totalName={totalName}
              headerName={GLOBAL_PERIOD_TABLE_HEADINGS_NAME}
              columnsNames={columnsNames || []}
              rows={fields}
              disabled={disabled}
              watchRowTitleName={variantsName}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              recalculateOnChangeRowIndexes={[1]}
            />
          </FormSectionFilledCard>
        </Fragment>
      </FullScreenWrapper>
    </FormSectionCard>
  );
};
